import React, { forwardRef, useEffect } from 'react';
import { Box as MuiBox, useTheme } from '@mui/material';
import Box from '@components/Atoms/Box';
import Paper from '@components/Atoms/Paper';
import BloomsSelector from '@components/Selectors/BloomsSelector/BloomsSelector';
import DokSelector from '@components/Selectors/DokSelector/DokSelector';
import BloomsSelectorWithCounts from '@components/Selectors/BloomsSelector/BloomsSelectorWithCounts';
import DokSelectorWithCounts from '@components/Selectors/DokSelector/DokSelectorWithCounts';
import FleschReadability from '@components/Selectors/FleschReadability/FleschReadability';
import FleschReadabilityWithCounts from '@components/Selectors/FleschReadability/FleschReadabilityWithCounts';
import FleschKincaid from '@components/Selectors/KincaidGrade/KincaidGrade';
import KincaidGradeWithCounts from '@components/Selectors/KincaidGrade/KincaidGradeWithCounts';
import GenericQuestionTypeSelector from '@components/Selectors/GenericQuestionTypeSelector/GenericQuestionTypeSelector';
import GenericQuestionTypeSelectorWithCounts from '@components/Selectors/GenericQuestionTypeSelector/GenericQuestionTypeSelectorWithCounts';
import ShowIf from '@components/Atoms/ShowIf';
import ContentGroupButton from './ContentGroupButton';
import {
  useContentFilterContext,
  useContentFilterLessonContext,
  useContentFilterSkillContext,
  useContentFilterStandardContext,
} from '../ContentFilterProvider';
import StandardPopover from './StandardPopover';
import SkillPopover from './SkillPopover';
import LessonPopover from './LessonPopover';

export type FilterType = 'lessons' | 'standards' | 'skills' | null;

const MyMuiBox = React.forwardRef<HTMLDivElement, any>((props, ref) => (
  <MuiBox ref={ref} {...props} />
));

MyMuiBox.displayName = 'Box';

export interface ContentFiltersProps {
  orientation?: 'horizontal' | 'vertical';
  disabled?: boolean;
  defaults?: {
    dok: string[];
    bloom: string[];
  };
  exclude?: string[];
  // Enable count display from Algolia refinements
  enableCounts?: boolean;
}

const ContentFilters = forwardRef<HTMLDivElement, ContentFiltersProps>((props, ref) => {
  const {
    orientation = 'horizontal',
    disabled = false,
    defaults,
    exclude = ['readability', 'grade'],
    enableCounts = false,
  } = props;
  const theme = useTheme();
  const [openFilterType, setOpenFilterType] = React.useState<FilterType>(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const {
    selectedBlooms,
    setSelectedBlooms,
    selectedDok,
    setSelectedDok,
    selectedQuestionType,
    setSelectedQuestionType,
    selectedReadability,
    setSelectedReadability,
    selectedGrade,
    setSelectedGrade,
  } = useContentFilterContext();
  const { selectedLessons } = useContentFilterLessonContext();
  const { selectedStandards } = useContentFilterStandardContext();
  const { selectedSkills } = useContentFilterSkillContext();

  const handleClose = () => {
    setAnchorEl(null);
  };

  const openFilter = (type: FilterType, element: HTMLElement) => {
    setOpenFilterType(type);
    setAnchorEl(element);
  };

  // set any defaults on mount
  useEffect(() => {
    if (defaults?.bloom) {
      setSelectedBlooms(defaults.bloom);
    }
    if (defaults?.dok) {
      setSelectedDok(defaults.dok);
    }
  }, [defaults?.bloom, defaults?.dok, setSelectedBlooms, setSelectedDok]);

  const isVerticalOrientation = orientation.toLowerCase() === 'vertical';

  // Helper function to check if a filter should be excluded
  const shouldExcludeFilter = (filterName: string) => {
    // Check explicit exclude list
    if (exclude && exclude.includes(filterName)) {
      return true;
    }
    return false;
  };

  return (
    <MyMuiBox
      ref={ref}
      sx={{
        display: 'flex',
        flexDirection: isVerticalOrientation ? 'column' : 'row',
        gap: 1,
      }}
    >
      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '0.5rem',
          p: 1,
          mb: isVerticalOrientation ? '0rem' : '1rem',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <ContentGroupButton
          type="lessons"
          count={selectedLessons.length}
          onClick={openFilter}
          color={theme.palette.filters.lessons}
        />

        <ContentGroupButton
          type="standards"
          count={selectedStandards.length}
          onClick={openFilter}
          color={theme.palette.filters.standards}
        />

        <ContentGroupButton
          type="skills"
          count={selectedSkills.length}
          onClick={openFilter}
          color={theme.palette.filters.skills}
        />
      </Paper>

      <Box
        sx={{
          mt: '0.4rem',
          display: 'flex',
          flexDirection: isVerticalOrientation ? 'column' : 'row',
          gap: isVerticalOrientation ? '0rem' : '0.625rem',
        }}
      >
        <ShowIf If={!shouldExcludeFilter('blooms')}>
          {enableCounts ? (
            <BloomsSelectorWithCounts
              value={selectedBlooms}
              onChange={(values) => setSelectedBlooms(values)}
              disabled={disabled}
              isMulti
              enableCounts
            />
          ) : (
            <BloomsSelector
              value={selectedBlooms}
              onChange={(values) => setSelectedBlooms(values)}
              disabled={disabled}
              isMulti
            />
          )}
        </ShowIf>

        <ShowIf If={!shouldExcludeFilter('dok')}>
          {enableCounts ? (
            <DokSelectorWithCounts
              value={selectedDok}
              onChange={(values) => setSelectedDok(values)}
              disabled={disabled}
              isMulti
              enableCounts
            />
          ) : (
            <DokSelector
              value={selectedDok}
              onChange={(values) => setSelectedDok(values)}
              disabled={disabled}
              isMulti
            />
          )}
        </ShowIf>

        <ShowIf If={!shouldExcludeFilter('questionType')}>
          {enableCounts ? (
            <GenericQuestionTypeSelectorWithCounts
              value={selectedQuestionType}
              onChange={(values) => setSelectedQuestionType(values)}
              disabled={disabled}
              isMulti
              enableCounts
            />
          ) : (
            <GenericQuestionTypeSelector
              value={selectedQuestionType}
              onChange={(values) => setSelectedQuestionType(values)}
              disabled={disabled}
              isMulti
            />
          )}
        </ShowIf>

        <ShowIf If={!shouldExcludeFilter('readability')}>
          {enableCounts ? (
            <FleschReadabilityWithCounts
              value={selectedReadability}
              onChange={(values) => setSelectedReadability(values)}
              disabled={disabled}
              isMulti
              enableCounts
            />
          ) : (
            <FleschReadability
              value={selectedReadability}
              onChange={(values) => setSelectedReadability(values)}
              disabled={disabled}
              isMulti
            />
          )}
        </ShowIf>

        <ShowIf If={!shouldExcludeFilter('grade')}>
          {enableCounts ? (
            <KincaidGradeWithCounts
              value={selectedGrade}
              onChange={(values) => setSelectedGrade(values)}
              disabled={disabled}
              isMulti
              enableCounts
            />
          ) : (
            <FleschKincaid
              value={selectedGrade}
              onChange={(values) => setSelectedGrade(values)}
              disabled={disabled}
              isMulti
            />
          )}
        </ShowIf>
      </Box>

      {/* Popover for Lesson Filter */}
      <LessonPopover
        open={openFilterType === 'lessons' && Boolean(anchorEl)}
        anchor={anchorEl}
        onClose={handleClose}
        disabled={disabled}
      />

      {/* Popover for Standard Filter */}
      <StandardPopover
        open={openFilterType === 'standards' && Boolean(anchorEl)}
        anchor={anchorEl}
        onClose={handleClose}
        disabled={disabled}
      />

      {/* Popover for Skill Filter */}
      <SkillPopover
        open={openFilterType === 'skills' && Boolean(anchorEl)}
        anchor={anchorEl}
        onClose={handleClose}
        disabled={disabled}
      />
    </MyMuiBox>
  );
});

// Add a display name for the component
ContentFilters.displayName = 'ContentFilters';

export default ContentFilters;
